import { useState, FC, useEffect } from 'react';
import {
  useMediaQuery,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  ListItemButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Add, Check } from '@mui/icons-material';
import clsx from 'clsx';
import { IExclusionOption } from '../../../../../models';
import { useParams } from 'react-router-dom';
import {
  createNeighborhoodSeriesSelectedExclusionOptions,
  removeNeighborhoodSeriesSelectedExclusionOptions
} from '../../../../../fetch';
import { useSnackbar } from 'notistack';
import { formatCapitalCase } from '../../../../../helpers';

interface IExcludedOptionItem {
  option: IExclusionOption;
  setId: number | null;
  exclusions: string[];
  refetch: () => void;
}

export const ExcludedOptionItem: FC<IExcludedOptionItem> = ({
  option,
  setId,
  exclusions,
  refetch
}) => {
  const classes = useStyles();
  const { neighborhoodId, selectedSeriesId }: { neighborhoodId: string; selectedSeriesId: string } =
    useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [isExcluded, setIsExcluded] = useState(exclusions.includes(option.value));

  const isMobile = useMediaQuery('(max-width: 960px)');

  useEffect(() => {
    setIsExcluded(exclusions.includes(option.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusions]);

  const handleClick = async () => {
    if (isExcluded) {
      await removeItem();
    } else {
      await createItem();
    }
  };

  const removeItem = async () => {
    try {
      await removeNeighborhoodSeriesSelectedExclusionOptions(neighborhoodId, selectedSeriesId, {
        optionName: option.name ? option.name : option.value,
        optionSetId: setId ?? null,
        sageID: setId ? null : option?.value
      });

      enqueueSnackbar(`Successfully included ${option.name ? option.name : option.value}`, {
        variant: 'success'
      });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error including ${option.name ? option.name : option.value}.`, {
        variant: 'error'
      });
    }
  };

  const createItem = async () => {
    try {
      await createNeighborhoodSeriesSelectedExclusionOptions(neighborhoodId, selectedSeriesId, {
        optionName: option.name ? option.name : option.value,
        optionSetId: setId ?? null,
        sageID: setId ? null : option?.value
      });
      enqueueSnackbar(`Successfully Excluded ${option.name ? option.name : option.value}`, {
        variant: 'success'
      });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error Excluding ${option.name ? option.name : option.value}.`, {
        variant: 'error'
      });
    }
  };

  return (
    <>
      <ListItemButton>
        <ListItemText
          classes={{ root: classes.listItem }}
          primary={formatCapitalCase(option.name ? option.name : option.value)}
        />
        <ListItemSecondaryAction className={classes.buttonWrapper}>
          <Button
            color="primary"
            variant={isExcluded ? 'contained' : 'text'}
            startIcon={isExcluded ? <Check /> : <Add />}
            className={clsx(classes.editButton, isExcluded && classes.selected)}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleClick();
            }}
          >
            {isMobile ? '' : `${isExcluded ? 'Excluded' : 'Exclude'}`}
          </Button>
        </ListItemSecondaryAction>
      </ListItemButton>
      <Divider />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
    paddingLeft: theme.spacing(4)
  },
  nested: {
    paddingLeft: theme.spacing(4)
  },
  listItem: {
    paddingTop: 2,
    paddingBottom: 2
  },
  listItemWithCats: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: theme.spacing(1)
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    marginRight: 8,
    padding: 0
  },
  openButton: {
    padding: 0
  },
  selected: {
    fontWeight: 'bold',
    padding: `2px 8px`,
    borderRadius: 5,
    marginRight: 0
  }
}));
